import { useEffect, useMemo, useState } from "react";
import { io } from "socket.io-client";
import { Link, useNavigate, useParams } from "react-router-dom";
import { Loader2, MessagesSquare, Smartphone, Wifi, WifiOff, Settings2 } from "lucide-react";
import { listSessoes, formatarTelefone } from "./services/whatsappChatApi";
import InboxLayout from "./components/InboxLayout";

// Página /whatsapp/chat[/:sessaoId]: chat do WhatsApp não-oficial (wppconnect),
// SEPARADO POR SESSÃO. Cada pill é uma sessão; a lista mostra só as conversas
// gravadas daquela sessão. Nada da Meta (que vive em /chat).
export default function WhatsAppChat() {
  const { sessaoId: sessaoIdParam } = useParams();
  const navigate = useNavigate();
  const [sessoes, setSessoes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [socket, setSocket] = useState(null);
  const [selectedConversaId, setSelectedConversaId] = useState(null);

  useEffect(() => {
    listSessoes()
      .then((d) => setSessoes(d))
      .catch(() => setSessoes([]))
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => {
    // Origem + path explícito '/api/socket.io/' (nginx só faz proxy de /api/*).
    const s = io(window.location.origin, { path: "/api/socket.io/", withCredentials: true });
    setSocket(s);
    return () => { s.disconnect(); };
  }, []);

  const sessaoId = useMemo(() => {
    const n = parseInt(sessaoIdParam, 10);
    return Number.isInteger(n) && n > 0 ? n : null;
  }, [sessaoIdParam]);

  // Sem :sessaoId na URL → redireciona pra primeira sessão (conectada de preferência).
  useEffect(() => {
    if (loading || sessoes.length === 0) return;
    const existe = sessaoId && sessoes.some((s) => s.id === sessaoId);
    if (existe) return;
    const alvo = sessoes.find((s) => s.status === "conectado") || sessoes[0];
    navigate(`/whatsapp/chat/${alvo.id}`, { replace: true });
  }, [loading, sessoes, sessaoId, navigate]);

  // Trocar de sessão fecha a conversa aberta.
  useEffect(() => { setSelectedConversaId(null); }, [sessaoId]);

  // Badge por sessão: zera quando uma mensagem é lida localmente é complexo;
  // mantemos o total vindo do backend e decrementamos quando o usuário abre uma conversa.
  useEffect(() => {
    if (!socket) return;
    function onNew(payload) {
      if (!payload?.sessao_id || payload?.mensagem?.direcao !== "inbound") return;
      if (Number(payload.sessao_id) === Number(sessaoId) && selectedConversaId === payload.conversa_id) return;
      setSessoes((prev) =>
        prev.map((s) => (s.id === Number(payload.sessao_id) ? { ...s, nao_lidas_total: (Number(s.nao_lidas_total) || 0) + 1 } : s)),
      );
    }
    socket.on("mensagem:new", onNew);
    return () => { socket.off("mensagem:new", onNew); };
  }, [socket, sessaoId, selectedConversaId]);

  const sessaoAtiva = sessoes.find((s) => s.id === sessaoId) || null;

  function handleSelectConversa(cid) {
    setSelectedConversaId(cid);
    if (cid !== null && cid !== undefined) {
      // Recalcula o badge da sessão de forma aproximada (o backend é a fonte da verdade no reload).
      setSessoes((prev) => prev.map((s) => (s.id === sessaoId && s.nao_lidas_total > 0 ? { ...s, nao_lidas_total: Math.max(0, s.nao_lidas_total - 1) } : s)));
    }
  }

  if (loading) {
    return (
      <div className="min-h-screen bg-base flex items-center justify-center sm:ml-16">
        <Loader2 className="w-6 h-6 animate-spin text-ink-4" />
      </div>
    );
  }

  if (sessoes.length === 0) {
    return (
      <div className="min-h-screen bg-base flex flex-col items-center justify-center text-ink-2 p-6 sm:ml-16">
        <Smartphone className="w-12 h-12 text-ink-5 mb-4" />
        <h2 className="text-xl font-semibold mb-2">Nenhuma sessão WhatsApp cadastrada</h2>
        <p className="text-sm text-ink-3 max-w-md text-center mb-6">
          Conecte um número por QR code em <strong>WhatsApp → Sessões</strong> para começar a gravar e ver as conversas aqui.
        </p>
        <Link to="/whatsapp/sessoes" className="btn-primary !py-2.5">Ir para Sessões</Link>
      </div>
    );
  }

  return (
    <div className="h-screen bg-base flex flex-col sm:ml-16">
      <header className="px-4 sm:px-6 py-3 border-b border-white/[0.06] flex flex-col gap-2 flex-shrink-0">
        <div className="flex items-center gap-3">
          <MessagesSquare className="w-5 h-5 text-brand-400" />
          <h1 className="text-lg font-semibold text-ink-1 font-display">Chat das sessões</h1>
          <span className="chip-neutral hidden sm:inline-flex">wppconnect</span>
          <Link
            to="/whatsapp/sessoes"
            className="ml-auto text-xs text-ink-3 hover:text-ink-1 inline-flex items-center gap-1 transition-colors"
            title="Configurar gravação das sessões"
          >
            <Settings2 className="w-3.5 h-3.5" />
            <span className="hidden sm:inline">Sessões</span>
          </Link>
        </div>

        <div className="flex items-center gap-2 overflow-x-auto scrollbar-none -mx-1 px-1" role="tablist" aria-label="Sessões">
          {sessoes.map((s) => {
            const ativa = s.id === sessaoId;
            const conectada = s.status === "conectado";
            const badge = Number(s.nao_lidas_total) || 0;
            return (
              <button
                key={s.id}
                type="button"
                role="tab"
                aria-selected={ativa}
                onClick={() => navigate(`/whatsapp/chat/${s.id}`)}
                className={`flex items-center gap-2 px-3 py-1.5 rounded-full border text-xs whitespace-nowrap transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-500 ${
                  ativa
                    ? "bg-brand-500/15 border-brand-500/40 text-ink-1 shadow-glow-sm"
                    : "bg-surface-2 border-white/[0.08] text-ink-3 hover:text-ink-1 hover:border-white/[0.16]"
                }`}
              >
                {conectada ? (
                  <Wifi className="w-3.5 h-3.5 text-positive-400" aria-label="Conectada" />
                ) : (
                  <WifiOff className="w-3.5 h-3.5 text-danger-400" aria-label="Desconectada" />
                )}
                <span className="font-medium">{s.nome}</span>
                {s.numero && <span className="text-ink-4 hidden sm:inline tabular-nums">{formatarTelefone(s.numero)}</span>}
                {badge > 0 && (
                  <span className="min-w-[18px] h-[18px] px-1 rounded-full bg-brand-600 text-white text-[10px] font-bold flex items-center justify-center">
                    {badge > 99 ? "99+" : badge}
                  </span>
                )}
              </button>
            );
          })}
        </div>

        {sessaoAtiva && sessaoAtiva.gravar_mensagens === false && (
          <p className="text-[11px] text-warning-400 flex items-center gap-1.5">
            Gravação desligada para esta sessão.
            <Link to="/whatsapp/sessoes" className="underline hover:text-warning-500">Ligar em Sessões</Link>
          </p>
        )}
      </header>

      <div className="flex-1 min-h-0">
        {sessaoId && sessaoAtiva ? (
          <InboxLayout
            key={sessaoId}
            sessaoId={sessaoId}
            socket={socket}
            selectedConversaId={selectedConversaId}
            onSelect={handleSelectConversa}
            gravacaoDesligada={sessaoAtiva.gravar_mensagens === false}
            wrapperClass="flex h-full bg-base overflow-hidden"
          />
        ) : (
          <div className="flex items-center justify-center h-full">
            <Loader2 className="w-5 h-5 animate-spin text-ink-5" />
          </div>
        )}
      </div>
    </div>
  );
}
